import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { TextFormField } from '~components/FormFields/TextFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { TREAD_UUID, useCompany } from '~hooks/useCompany/useCompany';
import { useStores } from '~store';
import UserStore from '~store/UserStore';
import { alert, AlertTypes } from '~types/AlertTypes';

const settingSchema = yup.object().shape({
  defaultProjectTerms: yup.string().max(4000).default(''),
});

type SettingDTO = yup.InferType<typeof settingSchema>;

function DefaultProjectTermsSetting() {
  const { userStore, toasterStore } = useStores();
  const { updateCompany } = useCompany();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const persistedValue = userStore.userCompany.defaultProjectTerms ?? '';

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SettingDTO>({
    resolver: yupResolver(settingSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { defaultProjectTerms: persistedValue },
  });

  const enterEditMode = () => {
    reset({ defaultProjectTerms: persistedValue });
    setIsEditing(true);
  };

  const exitEditMode = () => {
    reset({ defaultProjectTerms: persistedValue });
    setIsEditing(false);
  };

  // Wrapped by react-hook-form's handleSubmit so Yup's max(4000) runs before this fires.
  // Mirrors the AutoOrderCompletionSetting pattern: no try/catch — errors flow through
  // updateCompany → connection.handleRequestError, which emits the error toast once.
  // Wrapping in try/catch here produced a duplicate toast.
  const saveAndExit = (data: SettingDTO) => {
    const next = (data.defaultProjectTerms ?? '').trim();
    setIsSaving(true);
    updateCompany({
      company: {
        id: userStore.userCompany.id,
        defaultProjectTerms: next || null,
      },
    })
      .then(() => {
        reset({ defaultProjectTerms: next });
        setIsEditing(false);
        toasterStore.push(
          alert(
            t('administration.configuration.configuration_saved'),
            AlertTypes.success,
          ),
        );
      })
      // updateCompany routes failures through connection.handleRequestError, which
      // toasts + re-throws. The re-throw is intentional but already handled — swallow
      // it here so we don't leak an unhandled rejection (or double-toast).
      .catch(() => undefined)
      .finally(() => {
        setIsSaving(false);
      });
  };

  if (!isEditing) {
    return (
      <Box>
        {persistedValue ? (
          <Typography
            sx={{
              whiteSpace: 'pre-wrap',
              fontSize: '0.875rem',
              color: 'text.primary',
              mb: 1,
            }}
          >
            {persistedValue}
          </Typography>
        ) : (
          <Typography sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 1 }}>
            {t('administration.configuration.settings.default_project_terms.no_value')}
          </Typography>
        )}
        <SmallButton color="brandV2Yellow" onClick={enterEditMode}>
          {t('actions.edit')}
        </SmallButton>
      </Box>
    );
  }

  return (
    <Box>
      <TextFormField
        control={control}
        errors={errors}
        disabled={isSaving}
        multiline
        name="defaultProjectTerms"
        placeholder={t(
          'administration.configuration.settings.default_project_terms.placeholder',
        )}
        rows={6}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <SmallLoadingButton
          color="brandV2Yellow"
          loading={isSaving}
          onClick={handleSubmit(saveAndExit)}
        >
          {t('actions.save')}
        </SmallLoadingButton>
        <SmallButton
          color="secondary"
          variant="outlined"
          disabled={isSaving}
          onClick={exitEditMode}
        >
          {t('actions.cancel')}
        </SmallButton>
      </Stack>
    </Box>
  );
}

export default {
  component: DefaultProjectTermsSetting,
  canAccess: (userStore: UserStore) => {
    if (!userStore.getPermissions().canEditCompany) return false;
    // The Tread super-company cannot be updated via the standard endpoint (BE Pundit
    // guard returns 403). Hide the setting entirely for platform admins logged into
    // Tread to avoid a dead-end UI.
    return userStore.userCompany.id !== TREAD_UUID;
  },
};
